import { EventEmitter, Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoaderComponent } from '@shopping-angular-components/loader';
import { BasicLandingModel, CurrenciesContainer } from '../model/landingModel';
import { LandingModelService } from './landingModel.service';
import { ModalService } from '@shopping-angular-components/modal';
import { UpaService, UpaTarget } from './upa.service';
import { DiscreteFilter, Filter, FilterValue, OfferSortingOption, RangeFilter } from '../components/offers-filters/offers-filters-model';
import { Module } from 'projects/common/model/ModuleModel';
import { KeyValue } from '@angular/common';
import { SliderRange } from '../components/slider-range/slider-range.component';
import { ThousandsSeparatorPipe } from '../pipes/thousands-separator.pipe';
import { MulticurrencyComponent } from '../components/multicurrency/multicurrency.component';
import { OffersService } from './offers.service';
import { ActivatedRoute, Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

declare var window: any;

@Injectable({ providedIn: 'root' })
export class FiltersService {
  private static FILTER_URL = '/nymeria-gui/data/model?';
  private static FILTERS_MODULES: string[] = ['HotelFiltersModule', 'OffersFiltersModule', 'PackageFiltersModule', 'ManualFiltersModule'];

  forceVisible = {};

  filtersAppliedCountEvent: EventEmitter<{ count: number, filters: { [key: string]: Filter } }> = new EventEmitter<{ count: number, filters: { [key: string]: Filter } }>();
  modalFilterSelectedEvent: EventEmitter<{ filters: { [key: string]: Filter }, hasChanges: boolean, selectionsCount: number }> = new EventEmitter();
  filterUnappliedEvent: EventEmitter<{ criteria?: string, all?: boolean }> = new EventEmitter();
  filters: { [key: string]: Filter } = {};
  modalFilterSelections: { [key: string]: Filter } = {};
  filterQueryParamsMap: { [key: string]: string } = {}; // filtros ya aplicados
  selectedFilterLabels: { [key: string]: { label: string, count: number } } = {}; // filtros seleccionados pero quizás no aplicados
  currentCurrencyCode: string;
  thousandsSymbol: string;
  currencies: CurrenciesContainer;
  ignoreOutboundMonthRangesQueryParam = false;
  private removedFilters: Set<string> = new Set(); // filtros removidos por el usuario, para no pisarlos con lo forzado desde el back

  private defaultSortingId: string | null = null;
  private currentSortingId: string | null = null;

  constructor(private httpClient: HttpClient,
    private modalsService: ModalService,
    private upaService: UpaService,
    private landingModelService: LandingModelService,
    private thousandsSeparatorPipe: ThousandsSeparatorPipe,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: string) {

    this.currentCurrencyCode = this.landingModelService.getLandingModel().currency.code;
    this.currencies = this.landingModelService.getLandingModel().currencies;
    this.thousandsSymbol = this.landingModelService.getLandingModel().currency.thousandsSymbol;
  }

  initializeSortingState(sortingOptions: OfferSortingOption[], defaultSortingId: string | undefined): void {
    if (!sortingOptions || sortingOptions.length === 0 || !isPlatformBrowser(this.platformId)) {
      return;
    }

    const defaultOption = defaultSortingId ? defaultSortingId : sortingOptions.find(option => option.selected)?.id;
    this.defaultSortingId = defaultOption || null;

    const urlParams = new URLSearchParams(window.location.search);
    const sortingFromUrl = urlParams.get('sorting');

    if (sortingFromUrl) {
      this.currentSortingId = sortingFromUrl;
      if (sortingFromUrl !== this.defaultSortingId) {
        this.filterQueryParamsMap['sorting'] = sortingFromUrl;
      }
    } else {
      this.currentSortingId = this.defaultSortingId;
    }
  }

  registerFilters(filters: Record<string, Filter>) {
    if (!Object.keys(this.filters).length) {
      this.filters = filters;
    }

    this.applyFiltersFromUrlParams(this.filters);

    this.updateHasSelection(this.filters);

    Object.entries(filters)
      .forEach(([criteria, filter]) => {
        this.updateFilterQueryParamsMap(criteria, filter);
      });

    this.filtersAppliedCountEvent.emit({
      count: this.countFiltersApplied(),
      filters: this.filters
    });
  }

  updateModalFilterSelections(filters: { [key: string]: Filter }, hasChanges = false, selectionsCount = 0) {
    this.modalFilterSelections = filters;
    this.updateHasSelection(this.modalFilterSelections);
    this.modalFilterSelectedEvent.emit({ filters: this.modalFilterSelections, hasChanges, selectionsCount });
  }

  applyModalFilters(isImprovedFiltersEnabled?: boolean) {
    this.filters = this.modalFilterSelections;

    // Limpiar filterQueryParamsMap de filtros (preservando sorting)
    const sortingValue = this.filterQueryParamsMap['sorting'];
    this.filterQueryParamsMap = sortingValue ? { sorting: sortingValue } : {};

    // Sincronizar currentSortingId con el sorting preservado
    this.currentSortingId = sortingValue || this.defaultSortingId;

    // Actualizar con los filtros seleccionados en el modal
    Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
      this.updateFilterQueryParamsMap(filterKey, filterValue);
    });

    this.removeAppliedFiltersFromRemoved();
    this.closeModalIfOpen();
    LoaderComponent.showLoaderEvent.emit(true);


    return this.fetch(isImprovedFiltersEnabled);
  }

  updateFilterParams(moduleIndex: number, uniqueId?: string, track = true) {
    Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
      this.updateFilterQueryParamsMap(filterKey, filterValue);

      if (track) {
        this.upaService.trackClickTopic({
          label: filterKey + '_' + this.filterQueryParamsMap[filterKey],
          moduleId: 'OffersFiltersModule',
          moduleIndex: moduleIndex,
          target: UpaTarget.FILTER,
          uniqueId
        });
      }
    });
  }

  updateFilterQueryParamsMap(key: string, filter: Filter) {
    if (filter.type === 'discrete') {

      const selectedValues = filter.values.filter((value: FilterValue) => value.selected);
      // si entre los seleccionados está el 'all', se añade solo el 'all'
      const allItemsChoice = selectedValues.find(value => value.all_items_choice);
      this.updateOrDeleteParam(key, allItemsChoice ? [allItemsChoice?.id] : selectedValues.map(value => value.id));
    }
    else if (filter.type === 'range') {

      if (filter.selected) {
        Object.entries(filter.selected)
          .forEach(([rangeKey, { min, max }]) => {
            if (['outboundTimeRange', 'inboundTimeRange'].includes(rangeKey)) {
              this.updateOrDeleteParam(rangeKey, [`${min}-${max}`]);
            } else {
              this.updateOrDeleteParam(key, [`${min}-${max}`]); // sería precio, la rangeKey sería el currency
            }
          });
      } else {
        if (filter.value_type === 'TIME' && filter.multiselector) {
          ['outboundTimeRange', 'inboundTimeRange'].forEach(rangeKey => {
            this.updateOrDeleteParam(rangeKey, []);
          });
        } else {
          this.updateOrDeleteParam(key, []);
        }
      }
    }
    else if (filter.type === 'input') {

      this.updateOrDeleteParam(key, filter.value ? [filter.value] : []);
    }
  }

  updateOrDeleteParam(key: string, values: string[]) {
    if (values.length === 0) {
      delete this.filterQueryParamsMap[key];
    } else {
      this.filterQueryParamsMap[key] = values.join(',');
    }
  }

  applyFilter(isImprovedFiltersEnabled?: boolean): Promise<void> {
    this.removeAppliedFiltersFromRemoved();
    this.closeModalIfOpen();
    LoaderComponent.showLoaderEvent.emit(true);
    return this.fetch(isImprovedFiltersEnabled);
  }

  unapplyFilter(criteria: string, moduleIndex: number, uniqueId?: string, isImprovedFiltersEnabled?: boolean) {
    this.closeModalIfOpen();
    LoaderComponent.showLoaderEvent.emit(true);
    this.upaService.trackClickTopic({
      label: criteria + '_REMOVE',
      moduleId: 'OffersFiltersModule',
      moduleIndex: moduleIndex,
      target: UpaTarget.FILTER,
      uniqueId
    });

    this.emptySingleFilter(this.filters[criteria], criteria);
    this.removedFilters.add(criteria);
    this.filterUnappliedEvent.emit({ criteria });

    this.fetch(isImprovedFiltersEnabled);
  }

  unapplyFilterGroup(group: string, moduleIndex: number, uniqueId?: string, isImprovedFiltersEnabled?: boolean) {
    this.closeModalIfOpen();
    LoaderComponent.showLoaderEvent.emit(true);
    this.upaService.trackClickTopic({
      label: group + '_GROUP_REMOVE',
      moduleId: 'OffersFiltersModule',
      moduleIndex: moduleIndex,
      target: UpaTarget.FILTER,
      uniqueId
    });

    this.emptyFilterGroup(group);

    this.fetch(isImprovedFiltersEnabled);
  }

  unapplyAllFilter(moduleIndex: number, uniqueId?: string, isImprovedFiltersEnabled?: boolean): void {
    this.closeModalIfOpen();
    LoaderComponent.showLoaderEvent.emit(true);
    this.upaService.trackClickTopic({
      label: 'REMOVE_ALL',
      moduleId: 'OffersFiltersModule',
      moduleIndex: moduleIndex,
      target: UpaTarget.FILTER,
      uniqueId
    });

    const sortingValue = this.filterQueryParamsMap['sorting'];
    this.filterQueryParamsMap = sortingValue ? { sorting: sortingValue } : {};
    this.currentSortingId = sortingValue || this.defaultSortingId;

    this.emptyAllFilters();
    this.removedFilters = new Set(Object.keys(this.filters));
    this.filterUnappliedEvent.emit({ all: true });
    this.fetch(isImprovedFiltersEnabled);
  }

  private fetch(isImprovedFiltersEnabled?: boolean): Promise<void> {
    this.filtersAppliedCountEvent.emit({ count: this.countFiltersApplied(), filters: this.filters });
    return new Promise<void>((resolve, reject) => {
      const staticModules = this.landingModelService.getLandingModel().modules.filter((module) => !module.filterable);

      // resetear precio si cambia moneda: no sirve solo revisar el filterQueryParamsMap porque se puede estar quitando el filtro de moneda que estaba aplicado
      const isSecondaryCurrency = this.currencies.secondaryCurrency?.code === this.currentCurrencyCode;
      const newSelectedCurrencyCode = this.filterQueryParamsMap['currency'];
      const currencyChanged = newSelectedCurrencyCode && (this.currentCurrencyCode !== newSelectedCurrencyCode) || (!newSelectedCurrencyCode && isSecondaryCurrency);
      const priceRangeReset = currencyChanged && !!this.filterQueryParamsMap['priceRange'];
      if (priceRangeReset && this.filters.priceRange) {
        this.emptySingleFilter(this.filters.priceRange, 'priceRange');
      }
      this.initFiltersForcedVisibility();

      this.ignoreOutboundMonthRangesQueryParam = this.hasToIgnoreOutboundMonthRangesQueryParam();

      // Actualizar el parámetro 'filtered' ANTES de construir la URL del request
      this.updateFiltersParam(isImprovedFiltersEnabled);

      this.httpClient.get(this.getFilterUrl())
        // @ts-ignore
        .subscribe((response: BasicLandingModel) => {
          if (!response?.modules || response.modules.length === 0 || !response.modules.some(module => OffersService.useOfferModule(module.id) || module.id === 'FlightOffersModule')) {
            this.updateEmptyState(staticModules, true);
            // En caso de que haya módulos que deban actualizarse pero no contengan ofertas, por ej chanchitoBanner
            const modulesMerged = staticModules.concat(response?.modules || []).sort((a, b) => a.position - b.position);
            this.landingModelService.getLandingModel().modules = modulesMerged;
            this.landingModelService.updateModules(modulesMerged);

          } else {

            this.updateEmptyState(staticModules, false);
            const filterableModules = response.modules;
            const offersFiltersModule = filterableModules.find(module => module.id === 'OffersFiltersModule');

            if (offersFiltersModule) {
              if (currencyChanged && offersFiltersModule?.data?.filters?.priceRange) {
                this.emptySingleFilter(offersFiltersModule.data.filters.priceRange); //TODO directamente no mandar el precio
              } else {
                this.preserveCurrentRangeFilters(offersFiltersModule, this.filters);
              }
            }
            const modulesMerged = staticModules.concat(filterableModules).sort((a, b) => a.position - b.position);

            if (currencyChanged) {
              this.changeCurrentCurrency(newSelectedCurrencyCode || this.currencies.primaryCurrency.code);
            }

            this.landingModelService.updateModules(modulesMerged);
            this.filters = offersFiltersModule?.data.filters || this.filters;
            
            this.removedFilters.forEach(filterKey => {
              if (this.filters[filterKey]) {
                this.emptySingleFilter(this.filters[filterKey], filterKey);
              }
            });
          }

          this.syncQueryParamsToUrl();

          LoaderComponent.showLoaderEvent.emit(false);
          resolve();

        }, () => {
          LoaderComponent.showLoaderEvent.emit(false);
          reject();
        }, () => {
          this.filtersAppliedCountEvent.emit({ count: this.countFiltersApplied(), filters: this.filters }); // TODO duplicado?
          // se quita el filtro de la url si corresponde. De hacerlo antes del fetch, el cambio de url es pisado por el route de angular
          if (this.ignoreOutboundMonthRangesQueryParam) {
            this.removeQueryParam('outboundMonthRanges');
          }
        });
    });
  }

  preserveCurrentRangeFilters(offersFiltersModule: Module, filters: { [key: string]: Filter }) {
    Object.entries(filters).forEach(([key, filter]) => {
      if (filter.type === 'range') {
        offersFiltersModule.data.filters[key] = filter;
      }
    });
  }

  changeCurrentCurrency(newCurrencyCode) {
    this.currentCurrencyCode = newCurrencyCode;
    if (this.currencies.secondaryCurrency!.code !== newCurrencyCode) {
      MulticurrencyComponent.currencyEvent.emit(this.currencies.primaryCurrency); // TODO pasar a servicio?
    } else {
      MulticurrencyComponent.currencyEvent.emit(this.currencies.secondaryCurrency);
    }
  }

  updateEmptyState(modules: Module[], state: boolean) {
    modules.forEach((module) => {
      if (FiltersService.FILTERS_MODULES.includes(module.id)) {
        //TODO emprolijar condición, en el caso actual que usa modal de filtros (landing de vuelos) tiene múltiples empty state y no se usa el general
        if (module.id !== 'OffersFiltersModule' || !module.data?.use_modal) {
          module.data.showEmptyState = state;
        }
      }
    });
  }

  private getFilterUrl() {
    const currentParams = new URLSearchParams(location.search);

    // Identificar todos los filter keys conocidos
    const filterKeys = new Set<string>(['weeksRange', 'sorting', 'filtered', 'outboundTimeRange', 'inboundTimeRange']);
    Object.keys(this.filters).forEach(key => filterKeys.add(key));

    if (this.ignoreOutboundMonthRangesQueryParam) {
      filterKeys.add('outboundMonthRanges');
    }

    // Agregar también range filter keys específicos
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value.type == "range" && value.values) {
        Object.values(value.values).forEach((filterValue) => {
          Object.keys(filterValue).forEach(filterKey => {
            filterKeys.add(filterKey);
          });
        });
      }
    });

    // Extraer params externos (no-filtros) que debemos preservar
    const externalParams: { [key: string]: string } = {};
    currentParams.forEach((value, key) => {
      if (!filterKeys.has(key)) {
        externalParams[key] = value;
      }
    });

    // Construir query params desde cero en orden específico
    const query = new URLSearchParams();

    if (this.filterQueryParamsMap['filtered']) {
      query.set('filtered', this.filterQueryParamsMap['filtered']);
    }

    if (this.filterQueryParamsMap['sorting']) {
      query.set('sorting', this.filterQueryParamsMap['sorting']);
    }

    Object.entries(this.filterQueryParamsMap).forEach(([key, value]) => {
      if (key !== 'filtered' && key !== 'sorting') {
        query.set(key, value);
      }
    });

    Object.entries(externalParams).forEach(([key, value]) => {
      query.set(key, value);
    });

    return FiltersService.FILTER_URL
      .concat('originalUrl=' + location.pathname.replace('^/', ''))
      .concat('&' + query)
      .concat('&responseType=ONLY_FILTERED_CONTENT');
  }
 
  countFiltersApplied(): number {
    let count = 0;
    Object.entries(this.filterQueryParamsMap).forEach(([key, value]: [string, any]) => {
      // Excluir 'sorting' y 'filtered' del conteo de filtros
      if (key !== 'sorting' && key !== 'filtered') {
        count += value.split(',').length;
      }
    });
    return count;
  }

  filterPillsOrder(firstFilter: KeyValue<string, Filter>, secondFilter: KeyValue<string, Filter>) {
    if (firstFilter.key === 'flexible_cancellation_policy' || secondFilter.key === 'flexible_cancellation_policy') {
      return secondFilter.key === 'flexible_cancellation_policy' ? -1 : firstFilter.key === 'flexible_cancellation_policy' ? 1 : 0;
    }
    const firstFilterSelected = firstFilter.value.hasSelection || firstFilter.value.active;
    const secondFilterSelected = secondFilter.value.hasSelection || secondFilter.value.active;

    return firstFilterSelected && !secondFilterSelected ? -1 :
      secondFilterSelected && !firstFilterSelected ? 1 : 0;
  }

  filterOrder(firstFilter: KeyValue<string, Filter>, secondFilter: KeyValue<string, Filter>) {
    return secondFilter.key === 'flexible_cancellation_policy' ? -1 : firstFilter.key === 'flexible_cancellation_policy' ? 1 : 0;
  }

  updateFiltersState(filters: { [key: string]: Filter }, moduleIndex, uniqueId, track = true): void {
    this.filters = filters;

    this.updateHasSelection(this.filters);
    this.updateFilterParams(moduleIndex, uniqueId, track);
    this.filtersAppliedCountEvent.emit({ count: this.countFiltersApplied(), filters: this.filters });
  }

  updateHasSelection(filters: { [key: string]: Filter }): void {
    Object.values(filters).forEach(filter => {
      if (filter.type === 'discrete') {
        filter.hasSelection = filter.values.some(value => value.selected);
      } else if (filter.type === 'range') {
        filter.hasSelection = filter.selected != null;
      } else if (filter.type === 'input') {
        filter.hasSelection = filter.value != null && filter.value != ''
      }
    });
  }

  emptyAllFilters(filters?: { [key: string]: Filter }): void {
    Object.values(filters || this.filters).forEach(filter => {
      this.emptySingleFilter(filter);
    });
    this.filtersAppliedCountEvent.emit({ count: this.countFiltersApplied(), filters: this.filters });
  }

  emptyFilterGroup(group: string): void {
    Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
      if (filterValue.group_id === group) {
        this.emptySingleFilter(filterValue, filterKey);
      }
    });
    this.filtersAppliedCountEvent.emit({ count: this.countFiltersApplied(), filters: this.filters });
  }

  emptySingleFilter(filter: Filter, criteria?: string): void {
    filter.hasSelection = false;
    filter.active = false;
    if (filter.hidden && !filter.group_id) {
      Object.values(this.filters)
        .filter(f => !f.group_id)
        .forEach(f => f.hidden = f.forceHidden ?? false);
      this.forceVisible[filter.name] = !filter.forceHidden;
    }

    if (filter.type === 'discrete') {
      filter.values?.forEach(filterValue => { filterValue.selected = false; filterValue.disabled = false; });
    } else if (filter.type === 'range') {
      delete filter.selected;
    } else if (filter.type === 'input') {
      filter.value = '';
    }
    this.updateFilterQueryParamsMap(criteria || filter.name, filter);
  }

  getSelectedFilterLabels(tempFilters?: { [key: string]: Filter }, groupLabels: boolean = true): { [key: string]: { label: string, count: number } } {
    let selectedFilterLabels = {};
    Object.entries(tempFilters || this.filters).forEach(([filterKey, filterEntry]) => {
      let selectedValues: string[] = [];

      if (filterEntry.type === 'range' && filterEntry.selected) {

        Object.entries(filterEntry.selected as { [key: string]: SliderRange }).forEach(([key, value]) => {
          selectedValues.push(`${filterEntry.value[key]?.description || ''} ${value.minFormatted} - ${value.maxFormatted}`);
        });
      } else if (filterEntry.type === 'input' && filterEntry.value) {
        let value = filterEntry.value;
        if (['NUMBER', 'MONEY'].includes(filterEntry.input_type)) {

          value = this.thousandsSeparatorPipe.transform(value, this.thousandsSymbol);
          value = filterEntry.input_type === 'MONEY' ? `${this.currentCurrencyCode} ${value}` : value;
        }
        selectedValues.push(value);
      } else if ((filterEntry as DiscreteFilter).values?.some(value => value.selected)) {

        selectedValues = selectedValues.concat((filterEntry as DiscreteFilter).values
          ? (filterEntry as DiscreteFilter).values.filter(value => value.selected && !value.all_items_choice).map(value => value.name)
          : []
        );
      }

      const useCounter = selectedValues.length > 1 || filterKey.includes("weeksRange");

      if (selectedValues.length) {
        if (!groupLabels || !filterEntry.group_id) {
          selectedFilterLabels[filterKey] = {
            label: useCounter ? `${filterEntry.name} (${selectedValues.length})` : selectedValues[0],
            count: selectedValues.length
          };
        } else {
          selectedFilterLabels[filterEntry.group_id] = selectedFilterLabels[filterEntry.group_id] || { label: '', count: 0 };
          selectedFilterLabels[filterEntry.group_id].count = selectedFilterLabels[filterEntry.group_id].count + selectedValues.length;
          selectedFilterLabels[filterEntry.group_id].label = filterEntry.group_id + ' (' + selectedFilterLabels[filterEntry.group_id].count + ')';
        }
      }


    });
    if (!tempFilters) {
      this.selectedFilterLabels = selectedFilterLabels;
    }
    return selectedFilterLabels;
  }

  closeModalIfOpen() {
    if (this.modalsService.openedModals.length > 0) {
      this.modalsService.closeModal();
    }
  }

  initFiltersForcedVisibility() {
    Object.entries(this.filters).forEach(([filterKey, filterValue]) => {
      this.forceVisible[filterValue.name] = this.forceVisible[filterValue.name] || !filterValue.hidden;
    });
  }

  isForcedVisible(filterName: string): boolean {
    return this.forceVisible[filterName];
  }

  hasToIgnoreOutboundMonthRangesQueryParam() {
    const searchboxSelectedMonths = new URLSearchParams(location.search).get('outboundMonthRanges');

    if (!searchboxSelectedMonths) return false;

    const filteredFullMonths = this.getFilteredFullMonthsRanges();

    if (filteredFullMonths.join(',') === searchboxSelectedMonths) return false;

    return true;
  }

  getFilteredFullMonthsRanges(): string[] {
    const selectedWeekRanges = Object.entries(this.filterQueryParamsMap).filter(([key, value]) => key.includes('weeksRange')).map(([key, value]) => value);
    return selectedWeekRanges.map(range => {
      const weeks = range.split('-')[1];
      const days = parseInt(weeks[1]) - parseInt(weeks[0]);
      return days < 27 ? range : weeks[0].slice(0, 6);
    });
  }

  getFilteredFullMonths(): string[] {
    const ranges = this.getFilteredFullMonthsRanges();
    return ranges.map(range => {
      return range.slice(0, 6);
    });
  }

  removeQueryParam(queryParamName: string) {
    // es necesario usar el route de angular para no tener problemas de sync con el modal
    const currentParams = { ...this.route.snapshot.queryParams };
    if (currentParams[queryParamName]) {
      delete currentParams[queryParamName];

      this.router.navigate([], {
        queryParams: { ...currentParams },
        replaceUrl: true
      });
    }
  }

  applySorting(sortingId: string, moduleIndex: number, uniqueId?: string, isImprovedFiltersEnabled?: boolean): Promise<void> {
    LoaderComponent.showLoaderEvent.emit(true);

    this.upaService.trackClickTopic({
      label: sortingId,
      moduleId: 'OffersFiltersModule',
      moduleIndex: moduleIndex,
      target: UpaTarget.FILTER,
      uniqueId
    });

    // Actualizar sorting en estado
    this.currentSortingId = sortingId;

    // Actualizar query params
    if (sortingId === this.defaultSortingId) {
      delete this.filterQueryParamsMap['sorting'];
    } else {
      this.filterQueryParamsMap['sorting'] = sortingId;
    }

    return this.fetch(isImprovedFiltersEnabled);
  }

  private isDefaultState(): boolean {
    // Estado default = sorting default Y sin filtros aplicados
    const hasDefaultSorting = !this.currentSortingId ||
      this.currentSortingId === this.defaultSortingId;
    const hasNoFilters = !this.hasFiltersApplied();
    return hasDefaultSorting && hasNoFilters;
  }

  private hasFiltersApplied(): boolean {
    // Contar filtros aplicados (excluyendo 'sorting' y 'filtered')
    const appliedFilters = Object.keys(this.filterQueryParamsMap).filter(
      key => key !== 'sorting' && key !== 'filtered'
    );
    return appliedFilters.length > 0;
  }

  private updateFiltersParam(isImprovedFiltersEnabled?: boolean): void {
    if (isImprovedFiltersEnabled && !this.isDefaultState()) {
      this.filterQueryParamsMap['filtered'] = 'true';
    } else {
      delete this.filterQueryParamsMap['filtered'];
    }
  }

  private syncQueryParamsToUrl(): void {
    const url = new URL(window.location.href);
    const currentParams = new URLSearchParams(url.search);

    // Identificar todos los filter keys conocidos
    const allKnownFilterKeys = new Set<string>(['sorting', 'filtered', 'weeksRange', 'outboundTimeRange', 'inboundTimeRange']);
    Object.keys(this.filters).forEach(key => allKnownFilterKeys.add(key));

    // Agregar también range filter keys específicos
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value.type == "range" && value.values) {
        Object.values(value.values).forEach((filterValue) => {
          Object.keys(filterValue).forEach(filterKey => {
            allKnownFilterKeys.add(filterKey);
          });
        });
      }
    });

    // Extraer params externos (no-filtros) que debemos preservar
    const externalParams: { [key: string]: string } = {};
    currentParams.forEach((value, key) => {
      if (!allKnownFilterKeys.has(key)) {
        externalParams[key] = value;
      }
    });

    // Construir params desde cero en el orden correcto
    const orderedParams = new URLSearchParams();

    if (this.filterQueryParamsMap['filtered']) {
      orderedParams.set('filtered', this.filterQueryParamsMap['filtered']);
    }

    if (this.filterQueryParamsMap['sorting']) {
      orderedParams.set('sorting', this.filterQueryParamsMap['sorting']);
    }

    Object.entries(this.filterQueryParamsMap).forEach(([key, value]) => {
      if (key !== 'filtered' && key !== 'sorting' && value !== undefined && value !== null && value !== '') {
        orderedParams.set(key, String(value));
      }
    });

    Object.entries(externalParams).forEach(([key, value]) => {
      orderedParams.set(key, value);
    });

    url.search = orderedParams.toString();
    url.hash = ''; // Limpiar el hash del modal
    window.history.replaceState(null, '', url.toString());
  }

  private applyFiltersFromUrlParams(filters: { [key: string]: Filter }): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const urlParams = new URLSearchParams(window.location.search);

    Object.entries(filters).forEach(([filterKey, filter]) => {
      const paramValue = urlParams.get(filterKey);

      if (!paramValue) {
        if (filter.type === 'range' && filter.value_type === 'TIME' && filter.multiselector) {
          this.applyTimeRangeFromUrl(filter, urlParams);
        }
        return;
      }

      if (filter.type === 'discrete') {
        this.applyDiscreteFilterFromUrl(filter as DiscreteFilter, paramValue);
      } else if (filter.type === 'range') {
        this.applyRangeFilterFromUrl(filter as RangeFilter, paramValue, filterKey);
      } else if (filter.type === 'input') {
        this.applyInputFilterFromUrl(filter, paramValue);
      }
    });
  }

  private applyDiscreteFilterFromUrl(filter: DiscreteFilter, paramValue: string): void {
    const selectedIds = paramValue.split(',').map(id => id.trim());

    if (filter.values) {
      filter.values.forEach(value => {
        value.selected = selectedIds.includes(value.id);
      });
    }
  }

  private applyRangeFilterFromUrl(filter: RangeFilter, paramValue: string, filterKey: string): void {
    const [min, max] = paramValue.split('-').map(val => parseFloat(val.trim()));

    if (isNaN(min) || isNaN(max)) {
      return;
    }

    const currencyCode = this.currentCurrencyCode;

    if (filter.value && filter.value[currencyCode]) {
      const currencySymbol = this.currencies[currencyCode === this.currencies.primaryCurrency.code ? 'primaryCurrency' : 'secondaryCurrency']?.symbol || '';
      const minFormatted = currencySymbol + this.thousandsSeparatorPipe.transform(min, this.thousandsSymbol);
      const maxFormatted = currencySymbol + this.thousandsSeparatorPipe.transform(max, this.thousandsSymbol);

      filter.selected = {
        [currencyCode]: {
          ...filter.value[currencyCode],
          min: min,
          max: max,
          minFormatted: minFormatted,
          maxFormatted: maxFormatted
        }
      };
    }
  }

  private applyTimeRangeFromUrl(filter: RangeFilter, urlParams: URLSearchParams): void {
    if (!filter.values) return;

    const timeRangeKeys = ['outboundTimeRange', 'inboundTimeRange'];
    const selected: Record<string, SliderRange> = {};

    timeRangeKeys.forEach(rangeKey => {
      const paramValue = urlParams.get(rangeKey);
      if (paramValue && filter.values && filter.values[rangeKey]) {
        const [min, max] = paramValue.split('-').map(val => parseFloat(val.trim()));

        if (!isNaN(min) && !isNaN(max)) {
          selected[rangeKey] = {
            ...filter.values[rangeKey],
            min: min,
            max: max
          };
        }
      }
    });

    if (Object.keys(selected).length > 0) {
      filter.selected = selected;
    }
  }

  private applyInputFilterFromUrl(filter: Filter, paramValue: string): void {
    (filter as any).value = paramValue;
  }

  private removeAppliedFiltersFromRemoved(): void {
    Object.keys(this.filters).forEach(filterKey => {
      if (this.filters[filterKey].hasSelection) {
        this.removedFilters.delete(filterKey);
      }
    });
  }

}
